import type { ElementType, ReactNode } from 'react';

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  icon?: ElementType;
  actions?: ReactNode;
  size?: 'sm' | 'lg';
  className?: string;
}

const sizeStyles = {
  sm: {
    eyebrow: 'text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400',
    title: 'text-2xl font-semibold text-gray-900 dark:text-white',
    description: 'text-sm text-gray-600 dark:text-gray-300',
    icon: 'h-5 w-5',
  },
  lg: {
    eyebrow: 'text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400',
    title: 'text-3xl font-semibold text-gray-900 dark:text-white',
    description: 'text-sm text-gray-600 dark:text-gray-300',
    icon: 'h-7 w-7',
  },
} as const;

export const PageHeader = ({
  eyebrow,
  title,
  description,
  icon: Icon,
  actions,
  size = 'lg',
  className = '',
}: PageHeaderProps) => {
  const styles = sizeStyles[size];

  return (
    <div className={`flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ${className}`.trim()}>
      <div className="min-w-0">
        {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
        <div className="mt-1 flex items-center gap-3">
          {Icon && (
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/40 dark:text-primary-300 dark:ring-primary-800">
              <Icon className={styles.icon} aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <h1 className={styles.title}>{title}</h1>
            {description && <p className={`mt-1 max-w-3xl ${styles.description}`}>{description}</p>}
          </div>
        </div>
      </div>

      {actions && <div className="flex flex-wrap items-center gap-3 sm:justify-end">{actions}</div>}
    </div>
  );
};
